import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, serverTimestamp, getDoc, addDoc } from 'firebase/firestore';
import { onAuthStateChanged, getAuth } from 'firebase/auth';
import { db, auth } from '../firebase';
import '../styles/QuizSelection.css';
import { getCachedEspecialidadesTemas, loadQuizBank, revalidateQuizBank } from '../services/rtdbCache';
import { invalidateStudyQuestionsCache } from '../utils/studyDataService';

const translateHierarchy = (h) => {
  const map = { 'Marinero': 'MRO', 'Cabo': 'CBO', 'Tercer Maestre': '3RM', 'Segundo Maestre': '2DM', 'Primer Maestre': '1RM', 'Suboficial': 'SOF', 'Teniente': 'TTE', 'Capitán': 'CAP' };
  return map[h] || 'MRO';
};

const translateCorps = (c) => {
  const map = { 'Servicio de Administración e Intendencia Naval': 'SAIN', 'Infantería de Marina': 'INF', 'Servicio de Sanidad Naval': 'SAN', 'Servicio de Comunicaciones': 'COM', 'Servicio de Máquinas': 'MAQ' };
  return map[c] || 'SAIN';
};

const translateSpecialty = (s) => {
  const map = { 'Oficinista': 'OFTA', 'Lavandería': 'LAVD', 'Cocina': 'COCN', 'Armamento': 'ARMM', 'Navegación': 'NAVG' };
  return map[s] || 'OFTA';
};

const QuizSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [areas, setAreas] = useState([]);
  const [temas, setTemas] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [selectedArea, setSelectedArea] = useState(location.state?.selectedArea || null);
  const [isSharing, setIsSharing] = useState(false);
  const [sharingProgress, setSharingProgress] = useState(0);
  const [authUser, setAuthUser] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), (u) => {
      setAuthUser(u);
      setAuthInitialized(true);
    });
    return unsubscribe;
  }, []);

  const normalizeJerarquia = (str) => {
    try {
      return str
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[_\s]+DE[_\s]+/g, '_')
        .trim()
        .replace(/[_\s]+/g, '_');
    } catch { return str.toUpperCase(); }
  };

  const determineUserGrade = (g) => {
    const s = (g || '').toLowerCase().trim();
    if (s.includes('marinero') || s.includes('mro')) return 'marinero';
    if (s.includes('cabo') || s.includes('cbo')) return 'cabo';
    if (s.includes('tercer maestre') || s.includes('3rm') || s.includes('tercer_maestre')) return 'tercer_maestre';
    if (s.includes('segundo maestre') || s.includes('2dm') || s.includes('segundo_maestre')) return 'segundo_maestre';
    if (s.includes('primer maestre') || s.includes('1rm') || s.includes('primer_maestre')) return 'primer_maestre';
    if (s.includes('teniente de corbeta') || s.includes('tte.corb') || s.includes('teniente_corbeta')) return 'teniente_corbeta';
    if (s.includes('teniente de fragata') || s.includes('tte.frag') || s.includes('teniente_fragata')) return 'teniente_fragata';
    if (s.includes('teniente de navío') || s.includes('teniente de navio') || s.includes('ttn') || s.includes('teniente_navio') || s.includes('teniente_navío') || s.includes('tte.nav')) return 'teniente_navio';
    if (s.includes('capitán de corbeta') || s.includes('capitan de corbeta') || s.includes('ctc') || s.includes('capitan_corbeta') || s.includes('capitán_corbeta') || s.includes('cap.corb')) return 'capitan_corbeta';
    return null;
  };

  const matchesGrado = (tema, jerarquiaNombre) => {
    if (!tema.grados) return true;
    const gradosArray = tema.grados.split(',').map(g => normalizeJerarquia(g.trim()));
    const nombreNormalizado = normalizeJerarquia(jerarquiaNombre);
    return gradosArray.includes('TODOS') || gradosArray.includes(nombreNormalizado);
  };

  const fetchQuizData = useCallback(async () => {
    const applyBank = (bank, jerarquiaNombre, extraTemas = []) => {
      const validAreas = (bank.areas || []).filter((area) => area && area.nombre);
      let temasBase = (bank.temas || []).filter((tema) => tema && tema.nombre && tema.area_id);
      if (jerarquiaNombre) {
        temasBase = temasBase.filter((tema) => matchesGrado(tema, jerarquiaNombre));
      }
      setAreas(validAreas);
      setTemas([...temasBase, ...extraTemas]);
      setQuestions((bank.preguntas || []).filter((question) => question && question.pregunta));
    };

    try {
      setLoading(true);
      setError('');

      let userJerarquiaNombre = null;
      let hierarchyCode = 'MRO';
      let corpsCode = 'SAIN';
      let specialtyCode = 'OFTA';

      // Cache-first: IndexedDB/localStorage entrega el banco sin esperar a la red.
      const bank = await loadQuizBank();
      const hasCachedBank = (bank.temas?.length || 0) > 0 || (bank.preguntas?.length || 0) > 0;
      if (hasCachedBank) {
        applyBank(bank, null);
        setLoading(false);
      }

      if (authUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', authUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const userGrado = userData.grado || userData.hierarchy || userData.jerarquia || '';
            const userCuerpo = userData.corps || userData.cuerpo || userData.cuerpoServicio || '';
            const userEspecialidad = userData.specialty || userData.especialidad || '';

            const userGradeKey = determineUserGrade(userGrado);
            const HIERARCHY_TO_RTDB = {
              'marinero': 'MARINERO',
              'cabo': 'CABO',
              'tercer_maestre': 'TERCER_MAESTRE',
              'segundo_maestre': 'SEGUNDO_MAESTRE',
              'primer_maestre': 'PRIMER_MAESTRE',
              'suboficial': 'SUBOFICIAL',
              'teniente_corbeta': 'TENIENTE_CORBETA',
              'teniente_fragata': 'TENIENTE_FRAGATA',
              'teniente_navio': 'TENIENTE_NAVIO',
              'capitan_corbeta': 'CAPITAN_CORBETA'
            };
            if (userGradeKey) {
              hierarchyCode = HIERARCHY_TO_RTDB[userGradeKey] || userGrado.toUpperCase().replace(/\./g, '').replace(/\s+/g, '_');
              corpsCode = userCuerpo;
              specialtyCode = userEspecialidad;
            }

            const userGradoText = userData.grado || userData.hierarchy || userData.jerarquia || '';
            const userJerarquiaKey = determineUserGrade(userGradoText);
            if (userJerarquiaKey) {
              const JERARQUIAS = {
                marinero: { nombre: 'Marinero', search: 'MRO' },
                cabo: { nombre: 'Cabo', search: 'CBO' },
                tercer_maestre: { nombre: 'Tercer Maestre', search: '3RM' },
                segundo_maestre: { nombre: 'Segundo Maestre', search: '2DM' },
                primer_maestre: { nombre: 'Primer Maestre', search: '1RM' },
                teniente_corbeta: { nombre: 'Teniente de Corbeta', search: 'TTE.CORB.' },
                teniente_fragata: { nombre: 'Teniente de Fragata', search: 'TTE.FRAG.' },
                teniente_navio: { nombre: 'Teniente de Navío', search: 'TTE.NAV.' },
                capitan_corbeta: { nombre: 'Capitán de Corbeta', search: 'CAP.CORB.' }
              };
              userJerarquiaNombre = JERARQUIAS[userJerarquiaKey]?.nombre || null;
            }
          }
        } catch (e) {
          console.warn('[QuizSelection] No se pudo cargar jerarquía:', e.message);
        }
      }

      if (hasCachedBank) {
        applyBank(bank, userJerarquiaNombre);
      }

      // Cargar cuestionarios del usuario desde Firestore y especialidades en paralelo
      const [userQuizzesData, cachedEspec] = await Promise.all([
        (async () => {
          if (!authUser) return [];
          try {
            const userQuizzesRef = collection(db, 'user_quizzes', authUser.uid, 'quizzes');
            const userQuizzesSnapshot = await getDocs(userQuizzesRef);
            return userQuizzesSnapshot.docs
              .map((docSnap) => ({ id: docSnap.id, ...docSnap.data(), isCustom: true }))
              .filter((quiz) => !quiz.deleted);
          } catch (e) {
            console.warn('[QuizSelection] Error cargando quizzes personales:', e.message);
            return [];
          }
        })(),
        (async () => {
          if (!authUser) return [];
          try {
            return await getCachedEspecialidadesTemas(hierarchyCode, corpsCode, specialtyCode);
          } catch (e) {
            console.warn('[QuizSelection] Error cargando cuestionarios de especialidad:', e.message);
            return [];
          }
        })(),
      ]);

      const userQuizzesAsTemas = userQuizzesData.map((quiz) => ({
        id: quiz.id,
        nombre: quiz.name || quiz.temaName,
        area_id: parseInt(quiz.areaId, 10) || 1,
        isCustom: true,
        questionCount: quiz.questionCount,
      }));

      const specialtyQuizzesAsTemas = [];
      cachedEspec.forEach((temaObj) => {
        if (!temaObj?.cuestionarios) return;
        Object.entries(temaObj.cuestionarios).forEach(([quizId, quizObj]) => {
          specialtyQuizzesAsTemas.push({
            id: `specialty~~${hierarchyCode}~~${corpsCode}~~${specialtyCode}~~${temaObj.temaId}~~${quizId}`,
            nombre: `${temaObj.nombre || 'Especialidad'}: ${quizObj.titulo || 'Cuestionario'}`,
            area_id: 3,
            isSpecialty: true,
            questionCount: quizObj.preguntas ? (Array.isArray(quizObj.preguntas) ? quizObj.preguntas.length : Object.keys(quizObj.preguntas).length) : 0,
          });
        });
      });

      const extraTemas = [...userQuizzesAsTemas, ...specialtyQuizzesAsTemas];
      applyBank(bank, userJerarquiaNombre, extraTemas);
      setLoading(false);

      if (!hasCachedBank && bank.preguntas.length === 0) {
        setError('No se pudieron cargar los datos. Revisa tu conexión e inténtalo de nuevo.');
      }

      if (hasCachedBank) {
        // Revalidación silenciosa: solo se descarga el banco si cambió la versión.
        revalidateQuizBank()
          .then((fresh) => {
            if (fresh) applyBank(fresh, userJerarquiaNombre, extraTemas);
          })
          .catch(() => {});
      }
    } catch (error) {
      console.error('Error cargando datos desde Firebase:', error);
      setError(error instanceof Error ? error.message : 'Error al cargar los datos desde Firebase');

      // Fallback con datos básicos si Firebase falla
      if (areas.length === 0) {
        setAreas([
          { id: 1, nombre: 'Conocimientos Marineros' },
          { id: 2, nombre: 'Legislación' },
          { id: 3, nombre: 'Conocimientos Específicos' },
          { id: 4, nombre: 'Oficiales' }
        ]);
        setTemas([
          { id: 1, nombre: 'Estructura del Buque', area_id: 1 },
          { id: 2, nombre: 'Medidas del Buque', area_id: 1 },
          { id: 3, nombre: 'Constitución Política', area_id: 2 },
          { id: 4, nombre: 'Ley Orgánica de la Armada', area_id: 2 },
          { id: 5, nombre: 'DAM Recursos Humanos', area_id: 3 },
          { id: 6, nombre: 'DAM Adiestramiento', area_id: 3 },
          { id: 7, nombre: 'Temas Avanzados', area_id: 4 }
        ]);
      }
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  useEffect(() => {
    if (authInitialized) fetchQuizData();
  }, [fetchQuizData, authInitialized]);

  const getPreguntasCount = temaId => questions.filter(q => q.tema_id == temaId).length || 10;
  const getAreaTotalPreguntas = areaId => temas.filter(tema => tema.area_id === areaId).reduce((total, tema) => total + getPreguntasCount(tema.id), 0) || 30;
  const getAreaDescription = areaId => {
    switch (areaId) {
      case 1: return 'Conocimientos básicos de estructura de buques, medidas, navegación y procedimientos marítimos.';
      case 2: return 'Legislación naval, leyes orgánicas, reglamentos y normativas de la Armada de México.';
      case 3: return 'Conocimientos específicos DAM, recursos humanos, adiestramiento y temas especializados.';
      case 4: return 'Temas avanzados para oficiales superiores, incluyendo navegación estratégica y mando.';
      default: return 'Información sobre esta área de conocimiento naval.';
    }
  };
  const getAreaIcon = areaId => {
    switch (areaId) {
      case 1: return '⚓';
      case 2: return '📜';
      case 3: return '🎯';
      case 4: return '⭐';
      default: return '📚';
    }
  };
  const handleAreaSelect = areaId => setSelectedArea(areaId);
  const handleTemaSelect = tema => {
    // Unificación de UI: Todos los cuestionarios usan la misma interfaz de juego
    // Pasamos el areaId en el state para poder volver a la misma pantalla
    navigate(`/dashboard/quiz/${tema.id}`, { 
      state: { areaId: selectedArea } 
    });
  };
  const handleBackToAreas = () => setSelectedArea(null);

  const handleDeleteQuiz = async (e, quizId) => {
    e.stopPropagation(); // Evitar que inicie el quiz al borrar
    if (!await window.confirmAsync('¿Estás seguro de que quieres eliminar este cuestionario?')) return;

    try {
      setIsDeleting(true);
      const quizDocRef = doc(db, 'user_quizzes', auth.currentUser.uid, 'quizzes', quizId);
      await updateDoc(quizDocRef, {
        deleted: true,
        deletedAt: serverTimestamp()
      });

      // Actualizar estado local e invalidar el pool compartido de estudio.
      setTemas(prev => prev.filter(t => t.id !== quizId));
      invalidateStudyQuestionsCache();
    } catch (error) {
      console.error('Error al eliminar cuestionario:', error);
      alert('Error al eliminar el cuestionario');
    } finally {
      setIsDeleting(false);
    }
  };

  // Las funciones de traducción se movieron a nivel de módulo en la parte superior del archivo

  const handleShareToVault = async (e, quiz) => {
    e.stopPropagation();
    if (!auth.currentUser) return;

    try {
      // 1. Obtener perfil
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (!userDoc.exists()) {
        alert('Completa tu perfil antes de publicar en la Bóveda.');
        return;
      }

      const userData = userDoc.data();
      const hierarchyCode = translateHierarchy(userData.hierarchy || userData.jerarquia || 'MRO');
      const corpsCode = translateCorps(userData.corps || userData.cuerpo || 'SAIN');
      const specialtyCode = translateSpecialty(userData.specialty || userData.especialidad || 'OFTA');

      // 2. Pedir código de tema
      const themeCode = prompt('Ingresa un código corto para el tema (ej: NAVEGACION, LEYES):', quiz.nombre?.split(':')[1]?.trim()?.substring(0, 10).toUpperCase() || '');
      if (!themeCode) return;

      const standardizedId = `${hierarchyCode}.${corpsCode}.${specialtyCode}.${themeCode.toUpperCase().replace(/\s/g, '_')}_${auth.currentUser.displayName?.replace(/\s/g, '_') || 'USUARIO'}`;

      if (!await window.confirmAsync(`¿Publicar en la Bóveda Técnica como "${standardizedId}"?`)) return;

      setIsSharing(true);
      setSharingProgress(10);

      // 3. Crear entrada en shared_vault/{uid}/quizzes
      const vaultData = {
        standardizedId,
        hierarchyCode,
        corpsCode,
        specialtyCode,
        areaId: quiz.area_id || quiz.areaId,
        themeCode: themeCode.toUpperCase(),
        authorName: auth.currentUser.displayName || 'Usuario',
        authorId: auth.currentUser.uid,
        name: quiz.nombre || quiz.name,
        questionCount: quiz.questionCount,
        difficulty: quiz.difficulty || 'Media',
        timestamp: serverTimestamp(),
        isVault: true
      };

      const userVaultRef = collection(db, 'shared_vault', auth.currentUser.uid, 'quizzes');
      const vaultRef = await addDoc(userVaultRef, vaultData);
      setSharingProgress(30);

      // 4. Copiar preguntas
      const userQuestionsRef = collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes', quiz.id, 'questions');
      const userQuestionsSnap = await getDocs(userQuestionsRef);

      const vaultQuestionsRef = collection(db, 'shared_vault', auth.currentUser.uid, 'quizzes', vaultRef.id, 'questions');
      const totalQs = userQuestionsSnap.size;

      let processed = 0;
      for (const docSnap of userQuestionsSnap.docs) {
        const qData = docSnap.data();

        // Normalizar respuestas si es necesario (legacy 'opciones')
        const respuestasMap = {};
        if (qData.respuestas && typeof qData.respuestas === 'object' && !Array.isArray(qData.respuestas)) {
          // Ya está en formato nuevo
          Object.assign(respuestasMap, qData.respuestas);
        } else if (qData.opciones && Array.isArray(qData.opciones)) {
          // Legacy
          qData.opciones.forEach((opt, idx) => {
            const respId = 1000 + idx;
            respuestasMap[respId] = {
              id: respId,
              respuesta: opt,
              correcta: idx === (qData.correctIndex ?? qData.correctAnswer ?? 0) ? 1 : 0,
              preguntaId: 0
            };
          });
        }

        await addDoc(vaultQuestionsRef, {
          ...qData,
          respuestas: respuestasMap,
          addedAt: serverTimestamp()
        });

        processed++;
        setSharingProgress(30 + Math.floor((processed / totalQs) * 70));
      }

      setSharingProgress(100);
      setTimeout(() => {
        setIsSharing(false);
        setSharingProgress(0);
        alert('¡Cuestionario compartido exitosamente en la Bóveda Técnica!');
      }, 500);

    } catch (error) {
      console.error("Error sharing quiz:", error);
      alert(`Error al compartir: ${error.message}`);
      setIsSharing(false);
      setSharingProgress(0);
    }
  };

  if (loading) {
    return <div className="quiz-selection-container"><div className="loading">Cargando cuestionarios...</div></div>;
  }

  if (selectedArea !== null) {
    const area = areas.find(a => a.id === selectedArea);
    const temasDelArea = temas.filter(tema => tema.area_id === selectedArea);
    if (temasDelArea.length === 0) {
      return (
        <div className="quiz-selection-container">
          <button className="back-button" onClick={handleBackToAreas}>← Volver a Áreas</button>
          <h2>CUESTIONARIOS DE {area?.nombre || 'ÁREA SELECCIONADA'}</h2>
          <div className="error-message">No se encontraron temas para esta área. Por favor, selecciona otra área.</div>
        </div>
      );
    }
    return (
      <div className="quiz-selection-container">
        <button className="back-button" onClick={handleBackToAreas}>← Volver a Áreas</button>
        <h2>CUESTIONARIOS DE {area?.nombre}</h2>
        <div className="quiz-grid">
          {temasDelArea.map(tema => (
            <div
              className={`quiz-type ${tema.isCustom ? 'custom-quiz' : ''}`}
              key={tema.id}
              onClick={() => handleTemaSelect(tema)}
            >
              <div className="quiz-type-content">
                <div className="quiz-origin-tag">
                  {tema.isCustom ? '✨ Personalizado (IA)' : '🚢 Oficial'}
                </div>
                <h3>{tema.nombre}</h3>
                <div className="quiz-footer">
                  <div className="quiz-stats">
                    {tema.isCustom ? tema.questionCount : getPreguntasCount(tema.id)} preguntas
                  </div>
                  {tema.isCustom && (
                    <div className="custom-actions">
                      <button
                        className="share-quiz-btn"
                        onClick={(e) => handleShareToVault(e, tema)}
                        disabled={isSharing || isDeleting}
                        title="Compartir en Bóveda Técnica"
                      >
                        🛡️
                      </button>
                      <button
                        className="delete-quiz-btn"
                        onClick={(e) => handleDeleteQuiz(e, tema.id)}
                        disabled={isDeleting || isSharing}
                        title="Eliminar cuestionario personal"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Overlay de Compartir */}
        {isSharing && (
          <div className="quiz-sharing-overlay">
            <div className="loading-spinner"></div>
            <h3>Compartiendo en Bóveda...</h3>
            <div className="quiz-sharing-progress-bar-track">
              <div
                className="quiz-sharing-progress-bar-fill"
                style={{ width: `${sharingProgress}%` }}
              ></div>
            </div>
            <p>{sharingProgress}%</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="quiz-selection-container">
      <h2>Selecciona un Área de Conocimiento</h2>
      {error && <div className="error-message">{error}</div>}
      <div className="quiz-grid">
        {areas.map(area => (
          <div className="area-card" key={area.id} role="button" tabIndex={0} onClick={() => handleAreaSelect(area.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleAreaSelect(area.id); } }}>
            <div className="area-content">
              <div className="area-icon">{getAreaIcon(area.id)}</div>
              <h3>{area.nombre}</h3>
              <div className="area-stats">{getAreaTotalPreguntas(area.id)} preguntas</div>
              <p>{getAreaDescription(area.id)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuizSelection;